/*
 * Copyright (c) 2020. World Wide Generation Ltd
 */

import React, { useEffect } from 'react';
import { Route, useHistory } from 'react-router-dom';
import NotAuthorised from './not-authorised';
import Header from '@features/header';
import { LoginCallback, useOktaAuth } from '@okta/okta-react';
import { Loader } from '@g17eco/atoms/loader';
import { Button } from 'reactstrap';
import { useAppDispatch } from '../reducers';
import { logout } from '../actions/auth';
import { showLogin } from '../actions/login';
import { handleLoginCallbackError } from '../logger';

interface Error {
  name: string;
  errorCode?: string;
  errorSummary?: string
}

const LoginCallbackError = ({ error }: { error?: Error }) => {
  const history = useHistory();
  const { oktaAuth, authState } = useOktaAuth();

  const dispatch = useAppDispatch();
  const handleLogout = () => dispatch(logout('/'));

  // No stored transaction for this callback gives the first, a stored one that can't redeem the code the second
  const isUsedLogin = error?.errorSummary?.includes('PKCE codeVerifier') || error?.errorCode === 'invalid_grant';
  // Signed in, so a failed or replayed callback can move on; only access_denied is a real denial
  const shouldRedirect = authState?.isAuthenticated && error?.errorCode !== 'access_denied';

  useEffect(() => {
    if (error) {
      handleLoginCallbackError(error, isUsedLogin ? 'warning' : 'error');
    }
  }, [error, isUsedLogin]);

  useEffect(() => {
    if (shouldRedirect) {
      const state = new URLSearchParams(history.location.search).get('state') ?? undefined;
      const originalUri = oktaAuth.getOriginalUri(state);
      oktaAuth.removeOriginalUri(state);
      oktaAuth.options.restoreOriginalUri?.(oktaAuth, originalUri);
    }
  }, [history, oktaAuth, shouldRedirect]);

  if (!authState || shouldRedirect) {
    return <Loader />;
  }

  const getMessage = () => {
    if (!error) {
      return 'Something went wrong';
    }

    if (isUsedLogin) {
      return <>
        This usually happens when more than one login is started, for example in another tab, or when G17Eco is opened
        from a bookmark or shortcut to the login page. Close any other login tabs, then
        <Button size='sm' color='link' className='px-1 py-0 align-baseline' onClick={() => showLogin('/')}>
          log in again
        </Button>
        from the beginning. If you use a bookmark or shortcut, point it to {window.location.origin} instead.
      </>;
    }

    const { errorCode, errorSummary } = error;
    if (errorCode === 'access_denied') {
      return <>
        You do not have permission to access the platform. Please contact G17Eco support or
        <Button size='sm' color='link' onClick={handleLogout}>click here to logout</Button>
      </>;
    }
    return errorSummary;
  }

  return <>
    <Route path='/:root?/:unused?' component={Header} />
    <div className='main-container'>
      <NotAuthorised title={isUsedLogin ? 'This login has expired' : undefined} message={
        <>
          <i className='fa fa-exclamation-triangle mr-2' />{getMessage()}
        </>
      } />
    </div>
  </>;
};

const CustomLoginCallback = () => {
  const history = useHistory();

  if (!history.location.search) {
    return <LoginCallbackError />;
  }

  return <LoginCallback
    errorComponent={LoginCallbackError}
    loadingElement={<Loader />}
  />;
}

export default CustomLoginCallback;
